import cx from 'classnames';
import { useCallback, useContext, type FunctionComponent } from 'react';

import { useIsTemplateCard } from '@trello/business-logic-react/card';
import { useFeatureGate } from '@trello/feature-gate-client';
import { useCardId } from '@trello/id-context';
import { useSharedStateSelector } from '@trello/shared-state';

import { useIsCardMarkedComplete } from 'app/src/components/CardDoneState/useIsCardMarkedComplete';
import type { CardType } from 'app/src/components/CardType';
import { MirrorCardActions } from 'app/src/components/MirrorCard';
import { activeCardSharedState } from './activeCardSharedState';
import { CardFrontArchiveButton } from './CardFrontArchiveButton';
import { useCardFrontCardInfoFragment } from './CardFrontCardInfoFragment.generated';
import { CardFrontContext } from './CardFrontContext';
import { CardFrontEditButton } from './CardFrontEditButton';

// eslint-disable-next-line @trello/less-matches-component
import styles from './CardFront.less';

interface CardFrontActionsProps {
  cardType: CardType;
}

export const CardFrontActions: FunctionComponent<CardFrontActionsProps> = ({
  cardType,
}) => {
  const { value: isMirrorCardsEnabled } = useFeatureGate('xf_mirror_cards');

  const { editButtonRef } = useContext(CardFrontContext);

  const cardId = useCardId();
  const isCardActive = useSharedStateSelector(
    activeCardSharedState,
    useCallback(({ idActiveCard }) => idActiveCard === cardId, [cardId]),
  );

  const { data: card } = useCardFrontCardInfoFragment({
    from: { id: cardId },
  });

  const { value: isNewCardDoneStateFeatureEnabled } = useFeatureGate(
    'phx_new_card_done_state_workspace',
  );

  const isComplete = useIsCardMarkedComplete(cardId);
  const isArchived = card?.closed;
  const isTemplate = useIsTemplateCard(cardId);
  const isArchiveButtonVisible =
    isNewCardDoneStateFeatureEnabled &&
    isComplete &&
    !isArchived &&
    !isTemplate &&
    cardType !== 'separator';

  const isEditButtonVisible = !isArchived;
  const canShowArchiveButtonOnMirrorCard =
    isNewCardDoneStateFeatureEnabled && !isArchived;

  return (
    <div
      className={cx(
        styles.cardFrontActions,
        isCardActive && styles['cardFrontActions--isCardActive'],
      )}
    >
      {isEditButtonVisible && <CardFrontEditButton ref={editButtonRef} />}
      {isArchiveButtonVisible && <CardFrontArchiveButton />}
      {cardType === 'mirror' && isMirrorCardsEnabled && (
        <MirrorCardActions
          canShowArchiveButton={canShowArchiveButtonOnMirrorCard}
        />
      )}
    </div>
  );
};
